import { useState, useCallback } from 'react'
import { Link, router } from '@inertiajs/react'
import BorrowerLayout from '@/layouts/BorrowerLayout'
import BorrowerUpgradePrompt from '@/components/BorrowerUpgradePrompt'
import MetricCard from '@/components/debtlens/MetricCard'
import MaturityTimeline from '@/components/debtlens/MaturityTimeline'
import LoanCard from '@/components/debtlens/LoanCard'
import BorrowerUpgradeBanner from '@/components/debtlens/BorrowerUpgradeBanner'
import LoanForm from './LoanForm'
import { calculateTrueCost, type TrueCostResult, type TrueCostLoanType } from '@/lib/trueCostCalculations'

interface SyncPayment {
  amount: number
  date: string
  principal_portion: number
  interest_portion: number
  late_fee: number
  note: string | null
}

interface SyncData {
  lender_name: string
  current_balance: number
  payments_count: number
  last_payment_date: string | null
  payment_history: SyncPayment[]
}

interface BorrowerLoan {
  id: number
  lender_name: string
  loan_amount: number
  interest_rate: number
  term_months: number
  loan_type: string
  origination_date: string
  maturity_date: string
  origination_points: number
  monthly_payment: number
  status: string
  notes: string | null
  property_address: string | null
  property_type: string | null
  days_to_maturity: number
  maturity_urgency: 'red' | 'yellow' | 'green'
  auto_synced: boolean
  linked_loan_id: number | null
  sync_data?: SyncData
}

interface LenderMatch {
  lender_id: number
  lender_name: string
  loans: {
    loan_id: number
    borrower_name: string
    principal: number
    annual_rate: number
    term_months: number
    start_date: string
    remaining_balance: number
  }[]
}

interface AiReport {
  content: string
  generated_at: string
  error?: string
}

interface Stats {
  total_debt: number
  total_monthly: number
  weighted_avg_rate: number
}

interface Props {
  borrower_loans: BorrowerLoan[]
  stats: Stats
  is_pro: boolean
  is_portfolio: boolean
  can_add_loan: boolean
  loan_limit: number
  plan: string
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00')
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const LOAN_TYPE_LABELS: Record<string, string> = {
  bridge: 'Bridge',
  dscr: 'DSCR',
  interest_only: 'Interest Only',
  amortizing: 'Amortizing',
  other: 'Other',
}

function getTrueCostLoanType(loanType: string): TrueCostLoanType {
  return loanType === 'amortizing' ? 'amortizing' : 'interest_only'
}

function computeTrueCost(loan: BorrowerLoan): TrueCostResult {
  return calculateTrueCost({
    loanAmount: loan.loan_amount,
    interestRate: loan.interest_rate,
    termMonths: loan.term_months,
    loanType: getTrueCostLoanType(loan.loan_type),
    originationPoints: loan.origination_points || 0,
    flatFees: 0,
    holdMonths: loan.term_months,
    extensionFeePoints: 0,
    extensionPeriodMonths: 0,
    prepaymentPenalty: 0,
  })
}

export default function Dashboard({ borrower_loans, stats, is_pro, is_portfolio, can_add_loan, loan_limit, plan }: Props) {
  const [showForm, setShowForm] = useState(false)
  const [editingLoan, setEditingLoan] = useState<BorrowerLoan | null>(null)
  const [expandedCostLoan, setExpandedCostLoan] = useState<number | null>(null)
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const [exitStrategyLoading, setExitStrategyLoading] = useState<number | null>(null)
  const [exitStrategyReport, setExitStrategyReport] = useState<Record<number, AiReport>>({})
  const [expandedExitStrategy, setExpandedExitStrategy] = useState<number | null>(null)
  const [portfolioHealthLoading, setPortfolioHealthLoading] = useState(false)
  const [portfolioHealthReport, setPortfolioHealthReport] = useState<AiReport | null>(null)
  const [showPortfolioHealth, setShowPortfolioHealth] = useState(false)
  const [collapsedProperties, setCollapsedProperties] = useState<Set<string>>(new Set())
  const [lenderMatches, setLenderMatches] = useState<LenderMatch[] | null>(null)
  const [matchLoading, setMatchLoading] = useState(false)
  const [matchChecked, setMatchChecked] = useState(false)
  const [linkingLoan, setLinkingLoan] = useState<number | null>(null)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteSending, setInviteSending] = useState(false)
  const [inviteSent, setInviteSent] = useState(false)
  const [expandedPayments, setExpandedPayments] = useState<number | null>(null)

  const activeLoans = borrower_loans.filter((l) => l.status === 'active')
  const upcomingPayments = [...activeLoans]
    .sort((a, b) => a.maturity_date.localeCompare(b.maturity_date))
    .slice(0, 3)

  // Find the soonest maturity
  const nextMaturity = activeLoans.length > 0
    ? [...activeLoans].sort((a, b) => a.days_to_maturity - b.days_to_maturity)[0]
    : null

  function handleDelete(loanId: number) {
    if (!confirm('Are you sure you want to delete this loan?')) return
    router.delete(`/my-loans/loans/${loanId}`, {
      onError: () => alert('Failed to delete loan. Please try again.'),
    })
  }

  function handleEdit(loan: BorrowerLoan) {
    setEditingLoan(loan)
    setShowForm(true)
  }

  function handleAddLoan() {
    if (!can_add_loan) {
      setShowUpgradeModal(true)
      return
    }
    setEditingLoan(null)
    setShowForm(true)
  }

  function handleCloseForm() {
    setShowForm(false)
    setEditingLoan(null)
  }

  function getCsrfToken(): string {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
  }

  const handleExitStrategy = useCallback(async (loanId: number) => {
    if (!is_pro) return
    setExitStrategyLoading(loanId)
    try {
      const response = await fetch(`/my-loans/insights/exit-strategy/${loanId}`, {
        method: 'POST',
        headers: { 'X-CSRF-Token': getCsrfToken(), 'Accept': 'application/json' },
      })
      const data = await response.json()
      if (response.ok) {
        setExitStrategyReport((prev) => ({ ...prev, [loanId]: data }))
        setExpandedExitStrategy(loanId)
      } else {
        setExitStrategyReport((prev) => ({ ...prev, [loanId]: { content: '', generated_at: '', error: data.error || 'Failed to generate analysis.' } }))
        setExpandedExitStrategy(loanId)
      }
    } catch {
      setExitStrategyReport((prev) => ({ ...prev, [loanId]: { content: '', generated_at: '', error: 'Network error. Please try again.' } }))
      setExpandedExitStrategy(loanId)
    } finally {
      setExitStrategyLoading(null)
    }
  }, [is_pro])

  const handlePortfolioHealth = useCallback(async () => {
    if (!is_pro) return
    setPortfolioHealthLoading(true)
    setShowPortfolioHealth(true)
    try {
      const response = await fetch('/my-loans/insights/portfolio-health', {
        method: 'POST',
        headers: { 'X-CSRF-Token': getCsrfToken(), 'Accept': 'application/json' },
      })
      const data = await response.json()
      if (response.ok) {
        setPortfolioHealthReport(data)
      } else {
        setPortfolioHealthReport({ content: '', generated_at: '', error: data.error || 'Failed to generate report.' })
      }
    } catch {
      setPortfolioHealthReport({ content: '', generated_at: '', error: 'Network error. Please try again.' })
    } finally {
      setPortfolioHealthLoading(false)
    }
  }, [is_pro])

  const handleCheckLender = useCallback(async () => {
    setMatchLoading(true)
    setMatchChecked(true)
    try {
      const response = await fetch('/my-loans/links/check', {
        method: 'POST',
        headers: { 'X-CSRF-Token': getCsrfToken(), 'Accept': 'application/json' },
      })
      const data = await response.json()
      if (response.ok) {
        setLenderMatches(data.matches || [])
      }
    } catch {
      setLenderMatches([])
    } finally {
      setMatchLoading(false)
    }
  }, [])

  const handleLinkLoan = useCallback(async (lenderId: number, loanId: number) => {
    setLinkingLoan(loanId)
    try {
      const response = await fetch('/my-loans/links', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': getCsrfToken(),
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ user_id: lenderId, loan_id: loanId }),
      })
      if (response.ok) {
        router.reload()
      }
    } catch {
      // ignore
    } finally {
      setLinkingLoan(null)
    }
  }, [])

  const handleInviteLender = useCallback(async () => {
    if (!inviteEmail) return
    setInviteSending(true)
    try {
      const response = await fetch('/my-loans/links/invite_lender', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': getCsrfToken(),
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: inviteEmail }),
      })
      if (response.ok) {
        setInviteSent(true)
        setInviteEmail('')
      }
    } catch {
      // ignore
    } finally {
      setInviteSending(false)
    }
  }, [inviteEmail])

  function togglePropertyCollapse(address: string) {
    setCollapsedProperties((prev) => {
      const next = new Set(prev)
      if (next.has(address)) next.delete(address)
      else next.add(address)
      return next
    })
  }

  // Group loans by property (Portfolio tier only)
  const propertyGroups = (() => {
    const groups: Record<string, BorrowerLoan[]> = {}
    for (const loan of activeLoans) {
      const key = loan.property_address || '__unassigned__'
      if (!groups[key]) groups[key] = []
      groups[key].push(loan)
    }
    return groups
  })()

  const hasPropertyGrouping = Object.keys(propertyGroups).some((k) => k !== '__unassigned__')

  // Empty state
  if (borrower_loans.length === 0) {
    return (
      <BorrowerLayout>
        <div className="text-center py-16">
          <div className="w-20 h-20 mx-auto rounded-full bg-dl-accent/10 flex items-center justify-center">
            <svg className="w-10 h-10 text-dl-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
            </svg>
          </div>
          <h2 className="mt-6 text-xl font-bold text-gray-900">Add your first loan</h2>
          <p className="mt-2 text-gray-600 max-w-md mx-auto">
            Start tracking your private loan obligations, maturity dates, and true borrowing costs.
          </p>
          <button
            onClick={() => setShowForm(true)}
            className="mt-6 px-6 py-2.5 rounded-lg text-sm font-semibold text-gray-900 bg-dl-accent hover:bg-dl-accent-hover transition-colors"
          >
            Add a loan
          </button>
        </div>

        {showForm && <LoanForm onClose={handleCloseForm} isPro={is_pro} />}
      </BorrowerLayout>
    )
  }

  return (
    <BorrowerLayout>
      {/* Greeting + Add loan */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Your Loan Dashboard</h1>
          <p className="text-sm text-slate-500 mt-0.5">{activeLoans.length} of {loan_limit} loans active</p>
        </div>
        <button
          onClick={handleAddLoan}
          className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-900 bg-dl-accent hover:bg-dl-accent-hover transition-colors"
        >
          Add loan
        </button>
      </div>

      {/* Loan limit warning */}
      {!can_add_loan && (
        <BorrowerUpgradePrompt
          variant="banner"
          targetTier={plan === 'pro' ? 'portfolio' : 'pro'}
          title="Loan limit reached"
          message={`You've used all ${loan_limit} loans on the ${plan === 'pro' ? 'Pro' : plan === 'portfolio' ? 'Portfolio' : 'Free'} plan.`}
        />
      )}

      {/* 4 Metric Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <MetricCard label="Total Debt" value={formatCurrency(stats.total_debt)} />
        <MetricCard label="Monthly Cost" value={formatCurrency(stats.total_monthly)} />
        <MetricCard
          label="Avg Rate"
          value={stats.weighted_avg_rate > 0 ? `${stats.weighted_avg_rate.toFixed(2)}%` : '—'}
        />
        <MetricCard
          label="Next Maturity"
          value={nextMaturity ? `${nextMaturity.days_to_maturity}d` : '—'}
          sublabel={nextMaturity ? nextMaturity.lender_name : undefined}
        />
      </div>

      {/* Maturity Timeline Bar */}
      {activeLoans.length > 0 && (
        <div className="mb-6">
          <MaturityTimeline loans={activeLoans} />
        </div>
      )}

      {/* Connect with your lender */}
      <section className="mb-6">
        <div className="bg-gradient-to-r from-teal-50 to-emerald-50 rounded-xl border border-teal-200 p-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <svg className="w-4 h-4 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-gray-900">Connect with your lender</h3>
              <p className="text-xs text-gray-600 mt-0.5">
                If your lender uses LendSolo, you can sync your loan data automatically — see real-time balances and payment history.
              </p>

              {!matchChecked ? (
                <button
                  onClick={handleCheckLender}
                  disabled={matchLoading}
                  className="mt-3 px-4 py-2 rounded-lg text-xs font-semibold text-white bg-teal-600 hover:bg-teal-700 transition-colors disabled:opacity-50"
                >
                  {matchLoading ? 'Checking...' : 'Check if your lender uses LendSolo'}
                </button>
              ) : matchLoading ? (
                <div className="mt-3 flex items-center gap-2">
                  <svg className="w-4 h-4 text-teal-500 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  <span className="text-xs text-gray-600">Looking for matches...</span>
                </div>
              ) : lenderMatches && lenderMatches.length > 0 ? (
                <div className="mt-3 space-y-2">
                  <p className="text-xs font-medium text-teal-700">We found your lender on LendSolo!</p>
                  {lenderMatches.map((match) => (
                    <div key={match.lender_id} className="bg-white rounded-lg border border-teal-100 p-3">
                      <p className="text-sm font-semibold text-gray-900">{match.lender_name}</p>
                      <div className="mt-2 space-y-1.5">
                        {match.loans.map((loan) => (
                          <div key={loan.loan_id} className="flex items-center justify-between">
                            <div className="text-xs text-gray-600">
                              {formatCurrency(loan.principal)} at {loan.annual_rate}% &middot; {formatCurrency(loan.remaining_balance)} remaining
                            </div>
                            <button
                              onClick={() => handleLinkLoan(match.lender_id, loan.loan_id)}
                              disabled={linkingLoan === loan.loan_id}
                              className="text-xs font-semibold text-teal-600 hover:text-teal-700 disabled:opacity-50"
                            >
                              {linkingLoan === loan.loan_id ? 'Linking...' : 'Link this loan'}
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : matchChecked ? (
                <div className="mt-3">
                  <p className="text-xs text-gray-600 mb-2">
                    No matching lender found. Invite your lender to try LendSolo:
                  </p>
                  {inviteSent ? (
                    <p className="text-xs font-medium text-teal-600">Invitation sent!</p>
                  ) : (
                    <div className="flex gap-2">
                      <input
                        type="email"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        placeholder="lender@example.com"
                        className="flex-1 px-3 py-1.5 text-xs rounded-lg border border-gray-200 focus:border-teal-300 focus:ring-1 focus:ring-teal-200 outline-none"
                      />
                      <button
                        onClick={handleInviteLender}
                        disabled={inviteSending || !inviteEmail}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-teal-600 hover:bg-teal-700 transition-colors disabled:opacity-50"
                      >
                        {inviteSending ? 'Sending...' : 'Invite'}
                      </button>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio Health Report */}
      <section className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold text-gray-900">Portfolio Health</h2>
          {!is_pro && (
            <span className="text-xs font-semibold text-dl-accent bg-dl-accent/10 px-2 py-0.5 rounded-full">Pro</span>
          )}
        </div>
        {is_pro ? (
          <div className="bg-white rounded-xl border border-dl-border p-4">
            {!showPortfolioHealth ? (
              <div className="text-center py-2">
                <p className="text-sm text-gray-600 mb-3">
                  Get an AI-powered analysis of your entire loan portfolio — cost efficiency, risk flags, and refi candidates.
                </p>
                <button
                  onClick={handlePortfolioHealth}
                  className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 transition-colors"
                >
                  Generate portfolio health report
                </button>
              </div>
            ) : portfolioHealthLoading ? (
              <div className="flex items-center justify-center gap-2 py-6">
                <svg className="w-5 h-5 text-indigo-500 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                <span className="text-sm text-gray-600">Analyzing your portfolio (this may take 10-15 seconds)...</span>
              </div>
            ) : portfolioHealthReport?.error ? (
              <div className="rounded-lg bg-red-50 border border-red-200 p-3">
                <p className="text-sm text-red-600">{portfolioHealthReport.error}</p>
                <button onClick={handlePortfolioHealth} className="mt-2 text-xs font-medium text-red-700 hover:text-red-800">
                  Try again
                </button>
              </div>
            ) : portfolioHealthReport ? (
              <div>
                <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap">{portfolioHealthReport.content}</div>
                <div className="mt-4 flex items-center justify-between border-t border-gray-100 pt-3">
                  <p className="text-xs text-gray-400">
                    Generated {new Date(portfolioHealthReport.generated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </p>
                  <button onClick={handlePortfolioHealth} className="text-xs font-medium text-indigo-600 hover:text-indigo-700">
                    Regenerate
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        ) : (
          <BorrowerUpgradePrompt variant="inline" feature="Portfolio Health Report" />
        )}
      </section>

      {/* Property Grouping (Portfolio tier) */}
      {hasPropertyGrouping && (
        <section className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold text-gray-900">Properties</h2>
            {!is_portfolio && (
              <span className="text-xs font-semibold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">Portfolio</span>
            )}
          </div>
          {is_portfolio ? (
            <div className="space-y-3">
              {Object.entries(propertyGroups).map(([address, loans]) => {
                const label = address === '__unassigned__' ? 'Unassigned' : address
                const isCollapsed = collapsedProperties.has(address)
                const totalDebt = loans.reduce((s, l) => s + l.loan_amount, 0)
                const totalMonthly = loans.reduce((s, l) => s + l.monthly_payment, 0)
                const nextMat = loans.reduce((min, l) => Math.min(min, l.days_to_maturity), Infinity)

                return (
                  <div key={address} className="bg-white rounded-xl border border-dl-border overflow-hidden">
                    <button
                      onClick={() => togglePropertyCollapse(address)}
                      className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                      <div className="text-left">
                        <p className="text-sm font-semibold text-gray-900">{label}</p>
                        <p className="text-xs text-gray-500">
                          {loans.length} loan{loans.length !== 1 ? 's' : ''} &middot; {formatCurrency(totalDebt)} total &middot; {formatCurrency(totalMonthly)}/mo
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-gray-500">Next maturity: {nextMat}d</span>
                        <svg
                          className={`w-4 h-4 text-gray-400 transition-transform ${isCollapsed ? '' : 'rotate-180'}`}
                          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                        </svg>
                      </div>
                    </button>
                    {!isCollapsed && (
                      <div className="border-t border-gray-100 divide-y divide-gray-50">
                        {loans.map((loan) => (
                          <div key={loan.id} className="px-4 py-2.5 flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-gray-900">{loan.lender_name}</p>
                              <p className="text-xs text-gray-500">
                                {formatCurrency(loan.loan_amount)} &middot; {loan.interest_rate}% &middot; {loan.days_to_maturity}d to maturity
                              </p>
                            </div>
                            <p className="text-sm font-medium text-gray-700">{formatCurrency(loan.monthly_payment)}/mo</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          ) : (
            <BorrowerUpgradePrompt variant="inline" targetTier="portfolio" feature="Property Grouping" />
          )}
        </section>
      )}

      {/* Loan Cards */}
      <section className="mb-6">
        <h2 className="text-base font-semibold text-gray-900 mb-3">Active Loans</h2>
        <div className="space-y-3">
          {activeLoans
            .sort((a, b) => a.days_to_maturity - b.days_to_maturity)
            .map((loan) => (
              <div key={loan.id}>
                <LoanCard
                  id={loan.id}
                  lenderName={loan.lender_name}
                  loanAmount={loan.loan_amount}
                  interestRate={loan.interest_rate}
                  monthlyPayment={loan.monthly_payment}
                  originationPoints={loan.origination_points}
                  daysToMaturity={loan.days_to_maturity}
                  maturityUrgency={loan.maturity_urgency}
                  maturityDate={loan.maturity_date}
                  loanType={loan.loan_type}
                  autoSynced={loan.auto_synced}
                  syncData={loan.sync_data}
                  onEdit={() => handleEdit(loan)}
                  onDelete={() => handleDelete(loan.id)}
                  onExitStrategy={() => handleExitStrategy(loan.id)}
                  exitStrategyLoading={exitStrategyLoading === loan.id}
                  isPro={is_pro}
                />

                {/* Synced Payment History (expandable below card) */}
                {loan.auto_synced && loan.sync_data && loan.sync_data.payment_history.length > 0 && (
                  <div className="ml-4 mt-1">
                    <button
                      onClick={() => setExpandedPayments(expandedPayments === loan.id ? null : loan.id)}
                      className="text-xs font-medium text-teal-600 hover:text-teal-700"
                    >
                      {expandedPayments === loan.id ? 'Hide payments' : `View ${loan.sync_data.payments_count} payments`}
                    </button>
                    {expandedPayments === loan.id && (
                      <div className="mt-2 bg-white rounded-lg border border-dl-border p-3 space-y-1">
                        {loan.sync_data.payment_history.map((p, i) => (
                          <div key={i} className="flex items-center justify-between text-xs py-1">
                            <div className="text-gray-600">{formatDate(p.date)}</div>
                            <div className="text-right">
                              <span className="font-medium text-gray-900">{formatCurrency(p.amount)}</span>
                              <span className="text-gray-400 ml-1.5">
                                P: {formatCurrency(p.principal_portion)} / I: {formatCurrency(p.interest_portion)}
                                {p.late_fee > 0 && ` / Fee: ${formatCurrency(p.late_fee)}`}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Exit Strategy Report */}
                {expandedExitStrategy === loan.id && exitStrategyReport[loan.id] && (
                  <div className="mt-2 bg-white rounded-lg border border-dl-border p-4">
                    {exitStrategyReport[loan.id].error ? (
                      <div className="rounded-lg bg-red-50 border border-red-200 p-3">
                        <p className="text-xs text-red-600">{exitStrategyReport[loan.id].error}</p>
                      </div>
                    ) : (
                      <div>
                        <div className="prose prose-xs max-w-none text-gray-700 text-xs whitespace-pre-wrap">
                          {exitStrategyReport[loan.id].content}
                        </div>
                        <div className="mt-2 flex items-center justify-between">
                          <p className="text-[10px] text-gray-400">
                            Generated {new Date(exitStrategyReport[loan.id].generated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </p>
                          <div className="flex gap-2">
                            <button onClick={() => handleExitStrategy(loan.id)} className="text-[10px] font-medium text-indigo-600 hover:text-indigo-700">
                              Regenerate
                            </button>
                            <button onClick={() => setExpandedExitStrategy(null)} className="text-[10px] font-medium text-gray-400 hover:text-gray-600">
                              Close
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
        </div>
      </section>

      {/* True Cost Analysis */}
      <section className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold text-gray-900">True Cost Analysis</h2>
          {!is_pro && (
            <span className="text-xs font-semibold text-dl-accent bg-dl-accent/10 px-2 py-0.5 rounded-full">Pro</span>
          )}
        </div>

        {is_pro ? (
          <div className="space-y-3">
            {activeLoans.map((loan) => {
              const cost = computeTrueCost(loan)
              const isExpanded = expandedCostLoan === loan.id

              return (
                <div key={loan.id} className="bg-white rounded-xl border border-dl-border overflow-hidden">
                  <button
                    onClick={() => setExpandedCostLoan(isExpanded ? null : loan.id)}
                    className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <div className="text-left">
                      <p className="text-sm font-medium text-gray-900">{loan.lender_name}</p>
                      <p className="text-xs text-gray-500">
                        {formatCurrency(loan.loan_amount)} &middot; {loan.interest_rate}% &middot; {loan.term_months}mo
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="text-sm font-bold text-gray-900">{formatCurrency(cost.totalCost)}</p>
                        <p className="text-xs text-gray-500">total cost</p>
                      </div>
                      <svg
                        className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                        fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                      </svg>
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="px-4 pb-4 border-t border-gray-100">
                      <div className="mt-3 mb-4">
                        <div className="h-3 rounded-full overflow-hidden flex">
                          {cost.breakdown.map((item, i) => (
                            <div
                              key={i}
                              style={{ width: `${item.percentage}%`, backgroundColor: item.color }}
                              className="h-full"
                              title={`${item.label}: ${formatCurrency(item.amount)}`}
                            />
                          ))}
                        </div>
                        <div className="flex flex-wrap gap-3 mt-2">
                          {cost.breakdown.map((item, i) => (
                            <div key={i} className="flex items-center gap-1.5">
                              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }} />
                              <span className="text-xs text-gray-600">
                                {item.label}: {formatCurrency(item.amount)} ({item.percentage}%)
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <MiniStat label="Total Interest" value={formatCurrency(cost.totalInterest)} />
                        <MiniStat label="Origination Cost" value={formatCurrency(cost.originationCost)} />
                        <MiniStat label="Monthly Cost" value={formatCurrency(cost.monthlyCost)} />
                        <MiniStat label="Effective Annual Rate" value={`${cost.effectiveAnnualRate.toFixed(2)}%`} />
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        ) : (
          <div className="relative">
            <div className="blur-sm pointer-events-none select-none">
              {activeLoans.slice(0, 2).map((loan) => {
                const cost = computeTrueCost(loan)
                return (
                  <div key={loan.id} className="bg-white rounded-xl border border-dl-border p-4 mb-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{loan.lender_name}</p>
                        <p className="text-xs text-gray-500">{formatCurrency(loan.loan_amount)}</p>
                      </div>
                      <p className="text-sm font-bold text-gray-900">{formatCurrency(cost.totalCost)}</p>
                    </div>
                    <div className="h-3 rounded-full overflow-hidden flex mt-2">
                      {cost.breakdown.map((item, i) => (
                        <div key={i} style={{ width: `${item.percentage}%`, backgroundColor: item.color }} className="h-full" />
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <BorrowerUpgradePrompt variant="inline" feature="True Cost Analysis" />
            </div>
          </div>
        )}
      </section>

      {/* Next 3 Payments Due */}
      {upcomingPayments.length > 0 && (
        <section className="mb-6">
          <h2 className="text-base font-semibold text-gray-900 mb-3">Next 3 Payments Due</h2>
          <div className="bg-white rounded-xl border border-dl-border divide-y divide-gray-100">
            {upcomingPayments.map((loan) => {
              const dotColor = loan.maturity_urgency === 'red' ? 'bg-red-500' : loan.maturity_urgency === 'yellow' ? 'bg-amber-500' : 'bg-emerald-500'
              return (
                <div key={loan.id} className="px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className={`w-2 h-2 rounded-full ${dotColor}`} />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{loan.lender_name}</p>
                      <p className="text-xs text-gray-500">{LOAN_TYPE_LABELS[loan.loan_type] || loan.loan_type}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-gray-900">{formatCurrency(loan.monthly_payment)}</p>
                    <p className="text-xs text-gray-500">/ month</p>
                  </div>
                </div>
              )
            })}
          </div>
        </section>
      )}

      {/* Other Loans (non-active) */}
      {borrower_loans.some((l) => l.status !== 'active') && (
        <section className="mb-6">
          <h2 className="text-base font-semibold text-gray-900 mb-3">Other Loans</h2>
          <div className="bg-white rounded-xl border border-dl-border divide-y divide-gray-100">
            {borrower_loans
              .filter((l) => l.status !== 'active')
              .map((loan) => (
                <div key={loan.id} className="px-4 py-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{loan.lender_name}</p>
                    <p className="text-xs text-gray-500">
                      {formatCurrency(loan.loan_amount)} &middot; {loan.status.replace('_', ' ')}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => handleEdit(loan)} className="text-xs text-slate-400 hover:text-gray-600 px-1.5 py-0.5">
                      Edit
                    </button>
                    <button onClick={() => handleDelete(loan.id)} className="text-xs text-slate-400 hover:text-red-500 px-1.5 py-0.5">
                      Delete
                    </button>
                  </div>
                </div>
              ))}
          </div>
        </section>
      )}

      {/* Pro Upgrade Banner */}
      {!is_pro && <BorrowerUpgradeBanner plan={plan} />}

      {/* Loan Form Modal */}
      {showForm && <LoanForm loan={editingLoan} onClose={handleCloseForm} isPro={is_pro} />}

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <BorrowerUpgradePrompt
          variant="modal"
          targetTier={plan === 'pro' ? 'portfolio' : 'pro'}
          title="Upgrade to add more loans"
          message={`You've reached the ${loan_limit}-loan limit on the ${plan === 'pro' ? 'Pro' : 'Free'} plan.`}
          onClose={() => setShowUpgradeModal(false)}
        />
      )}
    </BorrowerLayout>
  )
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-slate-50 rounded-lg p-2.5">
      <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">{label}</p>
      <p className="text-sm font-bold text-gray-900 mt-0.5">{value}</p>
    </div>
  )
}
